import { Component, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

interface Props {
  children: ReactNode;
  // Changing this resets the boundary — we key it on the route path so
  // navigating away from a crashed view recovers without a full reload.
  resetKey: string;
}

interface State {
  error: Error | null;
}

/**
 * App-level safety net. React unmounts the entire tree when a render throws,
 * which would blank the whole dashboard (e.g. a corrupt span/timestamp from
 * ingestion). This catches that and shows an inline message instead, keeping
 * the shell nav usable so the operator can recover.
 */
class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error): void {
    // Surface for diagnostics; the rendered fallback is the user-facing part.
    console.error("Render error caught by ErrorBoundary:", error);
  }

  override componentDidUpdate(prev: Props): void {
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <section className="panel">
        <div className="panel-header">Something went wrong</div>
        <div style={{ padding: 16, display: "grid", gap: 12 }}>
          <div style={{ color: "var(--err)", whiteSpace: "pre-wrap" }}>
            {error.message || "Unexpected render error."}
          </div>
          <div style={{ color: "var(--muted)", fontSize: 12 }}>
            This view hit an unexpected error. Navigating to another page or
            reloading should recover.
          </div>
          <div>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                background: "transparent",
                color: "var(--muted)",
                border: "1px solid var(--border)",
                borderRadius: 4,
                padding: "6px 12px",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              Reload
            </button>
          </div>
        </div>
      </section>
    );
  }
}

/** Wraps children in an ErrorBoundary keyed on the current route path. */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}
